import { useCallback, useEffect, useRef, useState } from 'react'

import { TileType } from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'

import { getLineDataById } from '@/actions'
import { focusIds } from '@/constants/focus'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import { LineDataType, tileTypes } from '@/types/servicesTypes/configTypes'
import { SlideDataType } from '@/types/sliderTypes'
import { getTimezoneOffsetReversed } from '@/utils/utils'

export type UpdatedSliderData = SlideDataType

export const useUpdateSliderData = (
  initialLineData: LineDataType,
  allowViewAllTile: boolean,
) => {
  const { sliderId, data: initialSliderTiles, next } = initialLineData
  const limitTilesPerLine = useStore(
    (state) => state.common.appConfig.limitTilesPerLine,
  )
  const unsubscribeRef = useRef<() => void>(() => {})

  const [sliderData, setSliderData] = useState<UpdatedSliderData[]>([])

  const firstViewMoreTile = {
    id: `${focusIds.VIEW_MORE_FIRST}-${sliderId}`,
    type: tileTypes.SHOW_MORE,
  }
  const lastViewMoreTile = {
    id: `${focusIds.VIEW_MORE_LAST}-${sliderId}`,
    type: tileTypes.SHOW_MORE,
  }

  const updateSliderData = useCallback(
    async (shouldUpdateLineTiles = true) => {
      const timezoneOffsetReversed = getTimezoneOffsetReversed()
      unsubscribeRef.current()

      try {
        let lineTiles = shouldUpdateLineTiles
          ? await getLineDataById(
              sliderId,
              limitTilesPerLine,
              '',
              timezoneOffsetReversed,
            )
          : { items: initialSliderTiles, next }

        const { boughtEventIds, removeUpdatedEventIds } =
          usePPVPopupStore.getState()

        if (boughtEventIds.length) {
          const updatedEventIds: string[] = []

          lineTiles = {
            ...lineTiles,
            items: lineTiles.items.map((tile) => {
              const event = tile.data?.event
              if (event?.id && boughtEventIds.includes(event.id)) {
                if (event.userRights?.canAccess) {
                  updatedEventIds.push(event.id)
                  return tile
                }
                return {
                  ...tile,
                  data: {
                    ...tile.data,
                    event: {
                      ...event,
                      userRights: {
                        ...event.userRights,
                        canAccess: true,
                        reasons: [{ type: 'Ppv' }],
                      },
                    },
                  },
                } as TileType
              }
              return tile
            }),
          }

          removeUpdatedEventIds(updatedEventIds)
        }

        const tilesWithViewMore = allowViewAllTile
          ? [firstViewMoreTile, ...lineTiles.items, lastViewMoreTile]
          : [...lineTiles.items, lastViewMoreTile]

        const sliderData: Array<SlideDataType> = lineTiles.next
          ? tilesWithViewMore
          : lineTiles.items

        setSliderData(sliderData)

        unsubscribeRef.current = usePPVPopupStore.subscribe(
          (state, prevState) => {
            if (
              state.boughtEventIds &&
              state.boughtEventIds.length !== prevState.boughtEventIds.length &&
              sliderData.some(
                (tile) =>
                  tile.data?.event?.id &&
                  state.boughtEventIds.includes(tile.data?.event?.id),
              )
            ) {
              unsubscribeRef.current()
              updateSliderData(true)
            }
          },
        )
      } catch {
        console.error('Error: lines data backend response')
      }
    },
    [sliderId],
  )

  useEffect(() => {
    updateSliderData(false)
    return () => {
      unsubscribeRef.current()
    }
  }, [sliderId])

  return { sliderData, updateSliderData }
}
